import {
    MapTheme,
    InputViewBox,
    useFetchEtlDatasetLocationByDataSourceIdsQuery,
} from "@biggeo/bg-server-lib/studio";
import { Box } from "@biggeo/bg-ui/lab";
import { isSuccess } from "@vividtheory/remotedata";
import * as A from "fp-ts/lib/Array";
import { pipe } from "fp-ts/lib/function";
import isEqual from "lodash/isEqual";
import React from "react";
import { useParams } from "react-router";
import ErrorPage from "../../../common/components/ErrorPage";
import { getMapStyleFromSprite } from "../../views/MapStyles";
import { MapContextDataset, MapContextPolygon } from "../context/types";
import GenerateFile from "../components/GenerateFile";
import { getMultiFilters } from "../context/utils/general-utils";
import isEmpty from "lodash/isEmpty";

export interface IGenerateFileContainer {
    readonly openGenerateFilePage?: boolean;
    readonly savedViewId?: string;
    readonly setOpenGenerateFilePage?: (value: boolean) => void;
    readonly bounds?: mapboxgl.LngLatBounds;
    readonly viewport: InputViewBox;

    readonly draw: React.MutableRefObject<MapboxDraw | null>;
    readonly map: React.MutableRefObject<mapboxgl.Map | null>;
    readonly isMapLoaded: boolean;

    readonly mapStyle: MapTheme;
    readonly datasets: MapContextDataset[];

    readonly polygons: MapContextPolygon[];
}

const GenerateFileContainer = ({
    openGenerateFilePage,
    setOpenGenerateFilePage,
    bounds,
    datasets,
    viewport,

    isMapLoaded,

    mapStyle,
    map,
    polygons,
}: IGenerateFileContainer) => {
    const selectedDatasetIds = pipe(
        datasets,
        A.filter((d) => isEqual(d.isSelected, true)),
        A.map((sd) => sd.dataSource.id)
    );

    const { remote: etlLocationsRD } =
        useFetchEtlDatasetLocationByDataSourceIdsQuery(
            !isEmpty(selectedDatasetIds)
                ? {
                      variables: {
                          ids: selectedDatasetIds,
                      },
                  }
                : {
                      skip: true,
                  }
        );
    const etlLocations = isSuccess(etlLocationsRD)
        ? etlLocationsRD.data.fetchEtlDatasetLocationByDataSourceIds
        : [];

    const selectedDatasets = pipe(
        datasets,
        A.filter((d) => isEqual(d.isSelected, true)),
        A.map((sd) => ({
            ...sd,
            cloudStorageType: etlLocations.filter((e) =>
                isEqual(
                    sd.dataSourceConfiguration.fkDataSourceId,
                    e.dataSourceId
                )
            )[0]?.etlLocation?.cloudStorageInfo.cloudStorageType,
        }))
    );

    const { mapTemplateId } = useParams();

    if (!mapTemplateId) {
        return <ErrorPage />;
    }

    const multiFilters = getMultiFilters();

    const currentMapStyle =
        map.current && isMapLoaded ? map.current.getStyle() : undefined;

    const style = isEqual(
        getMapStyleFromSprite(currentMapStyle?.sprite),
        mapStyle
    )
        ? mapStyle
        : undefined;

    return (
        <Box
            sx={{
                width: "100vw",
                height: "100vh",
                position: "fixed",
                top: 0,
                left: 0,
                zIndex: 9999,
                overflow: "auto",
            }}
        >
            <Box
                sx={{
                    display: openGenerateFilePage ? "flex" : "none",
                    width: "100%",
                    height: "100%",
                    border: 1,
                    borderColor: (theme) => theme.palette.stroke[100],
                    borderRadius: 1,
                    boxShadow: (theme) => theme.shadows.allAround,
                }}
            >
                <GenerateFile
                    mapTemplateId={mapTemplateId}
                    datasets={datasets}
                    selectedDatasets={selectedDatasets}
                    viewport={viewport}
                    bounds={bounds}
                    polygons={polygons}
                    setOpenGenerateFilePage={setOpenGenerateFilePage}
                    style={style}
                    multiFilters={multiFilters}
                />
            </Box>
        </Box>
    );
};

export default GenerateFileContainer;
